import { Copy, AlertTriangle } from "lucide-react";
import { AnalysisUploader, HowItWorksCard, ResultsArea, DetailedAnalysisResults } from "@/components/analysis";
import { useToast } from "@/hooks/use-toast";
import { checkPlagiarism } from "@/services/fraudDetection";
import { DetectionResult } from "@/services/fraudDetection/types";
import { useContentAnalysis, AnalysisResult } from "@/hooks/useContentAnalysis";
import { ResultLevel } from "@/components/AnalysisResults";
import { determinePlagiarismLevel } from "@/utils/analysisUtils";
import { ErrorState } from "@/components/ui/error-state";
import { useApiError } from "@/hooks/use-api-error";

const PlagiarismCheck = () => {
  const { toast } = useToast();
  const { handleError, isError, error } = useApiError("PlagiarismCheck");
  
  const formatResults = (detectionResult: DetectionResult): AnalysisResult => {
    const level = determinePlagiarismLevel(detectionResult.score);
    
    return {
      results: [
        {
          type: "Web Content Match",
          level,
          score: Math.round(detectionResult.score * 100),
          details: detectionResult.flags.includes("Academic content patterns") 
            ? "Similarities with academic or published content detected." 
            : "Analysis of matching content from web sources.",
        },
        {
          type: "Content Originality",
          level: determinePlagiarismLevel(Math.max(0, detectionResult.score - 0.1)), // Slightly lower score
          score: Math.round(Math.max(0, (detectionResult.score - 0.1)) * 100),
          details: "Analysis of text patterns and writing style uniqueness.",
        },
        {
          type: "Linguistic Analysis",
          level: detectionResult.flags.includes("Low lexical diversity") 
            ? "suspicious" as ResultLevel 
            : "safe" as ResultLevel,
          score: detectionResult.flags.includes("Low lexical diversity") ? 65 : 25,
          details: detectionResult.flags.includes("Low lexical diversity")
            ? "Low linguistic diversity detected, which may indicate non-original content."
            : "Normal linguistic patterns consistent with original content.",
        },
      ],
      overallStatus: level,
      flags: detectionResult.flags,
      modelName: detectionResult.modelName,
      explanation: detectionResult.explanation,
      confidence: detectionResult.confidence,
      timestamp: detectionResult.timestamp,
    };
  };

  const { results, isLoading, handleAnalyze, resetResults } = useContentAnalysis<DetectionResult>(
    checkPlagiarism,
    formatResults,
    handleError
  );

  const handleExportResults = () => {
    toast({
      title: "Results Exported",
      description: "Plagiarism check results have been exported successfully",
      variant: "success"
    });
  };

  const plagiarismFeatures = [
    {
      title: "Web Content Matching",
      description: "Compares text against billions of web pages, news articles, and online publications."
    },
    {
      title: "Content Originality Analysis",
      description: "Uses linguistic analysis to identify paraphrased content and disguised copying."
    },
    {
      title: "Linguistic Analysis",
      description: "Examines writing style, vocabulary diversity, and phrasing patterns."
    },
    {
      title: "Academic Pattern Detection",
      description: "Identifies common academic writing patterns that may indicate plagiarism."
    }
  ];

  if (isError) {
    return (
      <ErrorState
        title="Plagiarism Check Error"
        description="There was a problem analyzing the content for plagiarism."
        error={error}
        onRetry={resetResults}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Plagiarism Check</h1>
        <p className="text-muted-foreground">
          Detect copied content and verify originality
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <AnalysisUploader
            title="Plagiarism Check"
            description="Upload or paste content to verify originality"
            acceptedFileTypes=".txt,.doc,.docx,.pdf"
            icon={<Copy className="h-5 w-5 text-fraudeye-accent" />}
            onAnalyze={handleAnalyze}
            allowText={true}
            isLoading={isLoading}
          />

          <HowItWorksCard 
            title="How It Works"
            description="Our plagiarism checker uses multiple detection methods"
            features={plagiarismFeatures}
          />
        </div>

        <div>
          {results ? (
            <>
              <DetailedAnalysisResults 
                results={results.results}
                overallStatus={results.overallStatus}
                flags={results.flags || []}
                modelName={results.modelName || "Plagiarism Detection Model"}
                explanation={results.explanation || "Detailed explanation not available."}
                confidence={results.confidence || 0.85}
                timestamp={results.timestamp || new Date()}
              />
              <div className="grid grid-cols-2 gap-4 mt-6">
                <button 
                  className="w-full px-4 py-2 border rounded-md hover:bg-primary-foreground transition-colors"
                  onClick={handleExportResults}
                >
                  Export Results
                </button>
                <button 
                  className="w-full px-4 py-2 bg-fraudeye-accent text-white rounded-md hover:bg-fraudeye-highlight transition-colors"
                  onClick={resetResults}
                >
                  New Analysis
                </button>
              </div>
            </>
          ) : (
            <ResultsArea
              results={null}
              icon={Copy}
              emptyTitle="No Analysis Results Yet"
              emptyDescription="Upload or paste content to begin plagiarism checking. Results will appear here once processing is complete."
              tipTitle="Tip"
              tipDescription="For more accurate analysis, provide longer text samples (500+ words)."
              onExport={handleExportResults}
              onNewAnalysis={resetResults}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PlagiarismCheck;
